import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, BookOpen, Cpu, Compass, Sun, Users, Building2, Handshake, Check, FileText, Hammer } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function AcademiePremium() {
 const tt = useT();

 const TRANSMISSION = [
 { i: BookOpen, t: tt("Guides", "Guides", "Guias"),
 d: tt("Des guides pratiques et pédagogiques, écrits pour être utilisés au quotidien.",
 "Practical, educational guides, written to be used every day.",
 "Guias práticos e pedagógicos, escritos para serem usados no dia-a-dia.") },
 { i: FileText, t: tt("Ressources", "Resources", "Recursos"),
 d: tt("Documents, supports et références pédagogiques accessibles à tous.",
 "Documents, materials and educational references accessible to everyone.",
 "Documentos, suportes e referências pedagógicas acessíveis a todos.") },
 { i: GraduationCap, t: tt("Programmes", "Programmes", "Programas"),
 d: tt("Des parcours co-construits avec les écoles, les entreprises et les partenaires.",
 "Pathways co-built with schools, businesses and partners.",
 "Percursos co-construídos com escolas, empresas e parceiros.") },
 { i: Hammer, t: tt("Ateliers", "Workshops", "Ateliers"),
 d: tt("Des rencontres pratiques, sur le terrain, pour apprendre en faisant.",
 "Practical, field-based encounters, to learn by doing.",
 "Encontros práticos, no terreno, para aprender fazendo.") },
 ];

 const FAMILLES = [
 { i: Sun, t: tt("Énergie éducative", "Educational energy", "Energia educativa"),
 d: tt("Sensibilisation à l'énergie, découverte du solaire, ateliers pratiques dans les écoles et les villages, culture des solutions durables.",
 "Energy awareness, discovery of solar, practical workshops in schools and villages, culture of sustainable solutions.",
 "Sensibilização para a energia, descoberta do solar, ateliers práticos nas escolas e aldeias, cultura das soluções sustentáveis.") },
 { i: Cpu, t: tt("Numérique", "Digital", "Digital"),
 d: tt("Culture numérique, bureautique, intelligence artificielle, préparation aux outils du monde du travail. Initiation dès le plus jeune âge.",
 "Digital literacy, office skills, artificial intelligence, preparation for workplace tools. Early-age introduction.",
 "Cultura digital, escritório, inteligência artificial, preparação para as ferramentas do mundo do trabalho. Iniciação desde a mais tenra idade.") },
 { i: BookOpen, t: tt("Lecture & culture", "Reading & culture", "Leitura e cultura"),
 d: tt("Accès aux ressources éducatives, ateliers de lecture et d'écriture, patrimoine ouest-africain, rencontres avec des auteurs.",
 "Access to educational resources, reading and writing workshops, West African heritage, meetings with authors.",
 "Acesso a recursos educativos, ateliers de leitura e escrita, património oeste-africano, encontros com autores.") },
 { i: Compass, t: tt("Orientation & avenir", "Guidance & future", "Orientação e futuro"),
 d: tt("Orientation scolaire et professionnelle, découverte des métiers, préparation du CV, de la lettre et des entretiens, immersions en entreprise.",
 "School and career guidance, profession discovery, CV and cover letter preparation, interview coaching, business immersions.",
 "Orientação escolar e profissional, descoberta de profissões, preparação do CV, da carta e das entrevistas, imersões em empresa.") },
 ];

 const AXES = [
 tt("Orientation scolaire et professionnelle", "School and career guidance", "Orientação escolar e profissional"),
 tt("Découverte des métiers", "Career discovery", "Descoberta de profissões"),
 tt("Culture numérique", "Digital literacy", "Cultura digital"),
 tt("Sensibilisation à l'énergie et aux solutions durables", "Awareness of energy and sustainable solutions", "Sensibilização para a energia e para as soluções sustentáveis"),
 tt("Entrepreneuriat et structuration de projets", "Entrepreneurship and project structuring", "Empreendedorismo e estruturação de projectos"),
 tt("Compétences personnelles et professionnelles", "Personal and professional skills", "Competências pessoais e profissionais"),
 tt("Leadership féminin", "Female leadership", "Liderança feminina"),
 tt("Préparation du CV et de la lettre de motivation", "CV and cover letter preparation", "Preparação do CV e da carta de motivação"),
 tt("Préparation aux entretiens", "Interview preparation", "Preparação para entrevistas"),
 tt("Rencontres et immersions professionnelles", "Meetings and business immersions", "Encontros e imersões profissionais"),
 tt("Lecture, culture et accès aux ressources éducatives", "Reading, culture and access to educational resources", "Leitura, cultura e acesso aos recursos educativos"),
 tt("Programmes co-construits avec les partenaires", "Programmes co-built with partners", "Programas co-construídos com os parceiros"),
 ];

 const PUBLICS = [
 { i: Users, t: tt("Jeunes & élèves", "Youth & students", "Jovens e alunos"),
 d: tt("Du collège au lycée, jusqu'à l'entrée dans le monde professionnel.",
 "From middle school to high school, up to entering the professional world.",
 "Do 3.º ciclo ao secundário, até à entrada no mundo profissional.") },
 { i: Building2, t: tt("Établissements scolaires", "Educational institutions", "Estabelecimentos escolares"),
 d: tt("Collèges, lycées, centres de formation, universités.",
 "Middle schools, high schools, training centres, universities.",
 "Colégios, liceus, centros de formação, universidades.") },
 { i: Handshake, t: tt("Entreprises & associations", "Businesses & associations", "Empresas e associações"),
 d: tt("Interventions, immersions, mentorat, mécénat de compétences.",
 "Interventions, immersions, mentoring, skills-based sponsorship.",
 "Intervenções, imersões, mentoria, mecenato de competências.") },
 { i: GraduationCap, t: tt("Porteurs de projets", "Project leaders", "Promotores de projectos"),
 d: tt("Entrepreneurs, artisans, femmes leaders, porteurs d'initiatives locales.",
 "Entrepreneurs, craftspeople, women leaders, local initiative leaders.",
 "Empreendedores, artesãos, mulheres líderes, promotores de iniciativas locais.") },
 ];

 const METHODE = [
 { n: "01", t: tt("Écoute des besoins", "Listening to needs", "Escuta das necessidades"),
 d: tt("Rencontre avec l'établissement, l'entreprise, l'association ou la collectivité.",
 "Meeting with the school, the business, the association or the local authority.",
 "Encontro com o estabelecimento, a empresa, a associação ou a autarquia.") },
 { n: "02", t: tt("Co-construction du programme", "Programme co-building", "Co-construção do programa"),
 d: tt("Adaptation des contenus, du format et du calendrier au contexte local.",
 "Adaptation of contents, format and schedule to the local context.",
 "Adaptação dos conteúdos, do formato e do calendário ao contexto local.") },
 { n: "03", t: tt("Intervention & animation", "Intervention & facilitation", "Intervenção e animação"),
 d: tt("Ateliers, conférences, immersions, rencontres, restitutions.",
 "Workshops, conferences, immersions, meetings, presentations.",
 "Ateliers, conferências, imersões, encontros, restituições.") },
 { n: "04", t: tt("Suivi & évaluation", "Follow-up & evaluation", "Acompanhamento e avaliação"),
 d: tt("Bilan avec les bénéficiaires, mesure d'impact, ajustements pour la suite.",
 "Review with beneficiaries, impact measurement, adjustments for the next steps.",
 "Balanço com os beneficiários, medida de impacto, ajustes para o seguimento.") },
 ];

 const PARTENAIRES = [
 tt("Écoles, collèges, lycées, centres de formation", "Schools, colleges, high schools, training centres", "Escolas, colégios, liceus, centros de formação"),
 tt("Collectivités locales et administrations", "Local authorities and administrations", "Autarquias locais e administrações"),
 tt("Associations et fondations", "Associations and foundations", "Associações e fundações"),
 tt("Entreprises et employeurs locaux", "Local businesses and employers", "Empresas e empregadores locais"),
 tt("Partenaires institutionnels", "Institutional partners", "Parceiros institucionais"),
 tt("Diaspora et mécènes", "Diaspora and patrons", "Diáspora e mecenas"),
 ];

 return (
 <div data-testid="page-academie-premium" className="bg-white">
 <Seo />

 {/* HERO, clair, éditorial */}
 <section className="bg-white">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-14 md:pb-20">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-8">NANDA SÁ Académie</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance max-w-4xl">
 {tt("Comprendre. Apprendre. Avancer.", "Understand. Learn. Move forward.", "Compreender. Aprender. Avançar.")}
 </h1>
 </FadeUp>
 <FadeUp delay={0.2}>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
 {tt("Guides, ressources, programmes et ateliers : l'Académie accompagne les jeunes, les écoles, les entreprises et les porteurs de projets au Sénégal et en Guinée-Bissau.",
 "Guides, resources, programmes and workshops: the Academy supports young people, schools, businesses and project leaders in Senegal and Guinea-Bissau.",
 "Guias, recursos, programas e ateliers: a Academia acompanha jovens, escolas, empresas e promotores de projectos no Senegal e na Guiné-Bissau.")}
 </p>
 </FadeUp>
 <FadeUp delay={0.3} className="mt-12 flex flex-wrap items-center gap-4">
 <Link to="/contact?subject=Academie-programme" className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300" data-testid="academie-cta-programme">
 {tt("Construire un programme avec nous", "Co-build a programme with us", "Construir um programa connosco")} <ArrowUpRight size={15} strokeWidth={1.5} />
 </Link>
 <a href="#familles" className="inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline" data-testid="academie-cta-familles">
 {tt("Voir les programmes", "See the programmes", "Ver os programas")}
 </a>
 </FadeUp>
 </div>
 <FadeUp delay={0.35} className="mx-auto max-w-7xl px-6 sm:px-12 pb-20 md:pb-28">
 <div className="overflow-hidden rounded-3xl">
 <img src="/photos/academie-hero.jpg" alt={tt("NANDA SÁ Académie, orientation et éducation au Sénégal et en Guinée-Bissau", "NANDA SÁ Académie, guidance and education in Senegal and Guinea-Bissau", "NANDA SÁ Académie, orientação e educação no Senegal e na Guiné-Bissau")} className="w-full aspect-[16/10] md:aspect-[16/7] object-cover" loading="eager" />
 </div>
 </FadeUp>
 </section>

 {/* MISSION */}
 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre mission", "Our mission", "A nossa missão")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Un espace pour apprendre et se former.", "A space to learn and train.", "Um espaço para aprender e formar-se.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] font-light leading-relaxed space-y-5">
 <p>{tt("NANDA SÁ Académie propose des guides, des ressources, des programmes et des ateliers. Chacun y apprend à son rythme, avec des supports concrets.",
 "NANDA SÁ Académie offers guides, resources, programmes and workshops. Everyone learns at their own pace, with concrete materials.",
 "A NANDA SÁ Académie propõe guias, recursos, programas e ateliers. Cada um aprende ao seu ritmo, com suportes concretos.")}</p>
 <p>{tt("Chaque programme est construit avec les écoles, les collectivités, les associations, les entreprises et les partenaires institutionnels.",
 "Every programme is built with schools, local authorities, associations, businesses and institutional partners.",
 "Cada programa é construído com as escolas, as autarquias, as associações, as empresas e os parceiros institucionais.")}</p>
 </FadeUp>
 </div>
 </Section>

 {/* TRANSMISSION, guides, ressources, programmes, ateliers */}
 <Section className="bg-nanda-bgAlt" id="transmission">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("L'espace de transmission", "The transmission space", "O espaço de transmissão")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Quatre façons d'apprendre.", "Four ways to learn.", "Quatro formas de aprender.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
 {TRANSMISSION.map((f, i) => (
 <StaggerItem key={f.t} className="bg-white border border-black/[0.06] rounded-3xl p-8 md:p-10 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500" data-testid={`transmission-${i}`}>
 <f.i size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-6" />
 <div className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink leading-tight">{f.t}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft font-light leading-relaxed">{f.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* FAMILLES DE PROGRAMMES */}
 <Section id="familles">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos quatre familles de programmes", "Our four programme families", "As nossas quatro famílias de programas")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Quatre familles de programmes.", "Four programme families.", "Quatro famílias de programas.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {FAMILLES.map((f) => (
 <StaggerItem key={f.t} className="bg-white border border-black/[0.06] rounded-3xl p-10 md:p-12 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500">
 <f.i size={30} strokeWidth={1.2} className="text-nanda-terracotta mb-6" />
 <div className="font-sans font-medium tracking-tight text-2xl md:text-3xl text-nanda-ink leading-tight">{f.t}</div>
 <p className="mt-5 text-[15px] text-nanda-inkSoft font-light leading-relaxed">{f.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* AXES */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos principaux axes", "Our main axes", "Os nossos principais eixos")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Douze axes de travail concrets.", "Twelve concrete lines of work.", "Doze eixos de trabalho concretos.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
 {AXES.map((a) => (
 <div key={a} className="flex items-start gap-3 py-3 border-t border-nanda-line">
 <Check size={16} strokeWidth={2} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-[15px] text-nanda-ink font-light leading-snug">{a}</span>
 </div>
 ))}
 </div>
 </Section>

 {/* PUBLICS */}
 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos publics", "Our audiences", "Os nossos públicos")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Quatre publics, un même objectif.", "Four audiences, one same objective.", "Quatro públicos, um mesmo objectivo.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
 {PUBLICS.map((p) => (
 <StaggerItem key={p.t} className="bg-white border border-black/[0.06] rounded-3xl p-8 md:p-10 hover:border-black/20 transition-all duration-500">
 <p.i size={26} strokeWidth={1.2} className="text-nanda-ink mb-5" />
 <div className="font-sans font-medium tracking-tight text-xl text-nanda-ink leading-tight">{p.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft font-light leading-relaxed">{p.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* MÉTHODE */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Notre méthode", "Our method", "O nosso método")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Quatre étapes co-construites.", "Four co-built steps.", "Quatro etapas co-construídas.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 {METHODE.map((m) => (
 <StaggerItem key={m.n} className="bg-white border border-black/[0.06] rounded-3xl p-8 md:p-10">
 <div className="font-sans font-light text-nanda-terracotta text-4xl md:text-5xl mb-5">{m.n}</div>
 <div className="font-sans font-medium tracking-tight text-xl text-nanda-ink leading-tight">{m.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft font-light leading-relaxed">{m.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 {/* PARTENAIRES */}
 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Avec qui nous travaillons", "Who we work with", "Com quem trabalhamos")}</Overline>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-balance">
 {tt("Six familles de partenaires.", "Six families of partners.", "Seis famílias de parceiros.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line rounded-3xl overflow-hidden">
 {PARTENAIRES.map((p) => (<div key={p} className="bg-white p-6 md:p-8"><div className="font-sans font-normal text-lg md:text-xl text-nanda-ink">{p}</div></div>))}
 </div>
 </Section>

 {/* CTA FINAL, clair */}
 <Section className="bg-nanda-bgAlt">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-terracotta">{tt("Nous accompagner", "Support us", "Acompanhe-nos")}</span></Overline>
 <h3 className="font-sans font-light tracking-tight text-3xl md:text-5xl leading-[1.1] mt-4 text-nanda-ink text-balance">
 {tt("Vous êtes une école, une entreprise, une association ?", "Are you a school, a business, an association?", "É uma escola, uma empresa, uma associação?")}
 </h3>
 <p className="mt-8 text-nanda-inkSoft text-lg font-light leading-relaxed max-w-3xl mx-auto">
 {tt("Contactez-nous pour construire ensemble un programme utile à votre territoire.",
 "Contact us to build together a programme that is useful for your territory.",
 "Contacte-nos para construirmos juntos um programa útil para o vosso território.")}
 </p>
 <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
 <Link to="/contact?subject=Academie-programme" className="inline-flex items-center justify-center gap-2 bg-nanda-terracotta text-white rounded-full px-6 py-4 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300" data-testid="academie-cta-1">
 {tt("Construire un programme", "Co-build a programme", "Construir um programa")}
 </Link>
 <Link to="/contact?subject=Academie-partenaire" className="inline-flex items-center justify-center gap-2 border border-nanda-line bg-white text-nanda-ink rounded-full px-6 py-4 text-sm font-medium hover:border-nanda-ink transition-colors duration-300" data-testid="academie-cta-2">
 {tt("Devenir partenaire", "Become a partner", "Tornar-se parceiro")}
 </Link>
 <Link to="/contact?subject=Academie-intervention" className="inline-flex items-center justify-center gap-2 border border-nanda-line bg-white text-nanda-ink rounded-full px-6 py-4 text-sm font-medium hover:border-nanda-ink transition-colors duration-300" data-testid="academie-cta-3">
 {tt("Accueillir une intervention", "Host an intervention", "Acolher uma intervenção")}
 </Link>
 <Link to="/contact?subject=Academie-projet" className="inline-flex items-center justify-center gap-2 border border-nanda-line bg-white text-nanda-ink rounded-full px-6 py-4 text-sm font-medium hover:border-nanda-ink transition-colors duration-300" data-testid="academie-cta-4">
 {tt("Présenter un projet", "Present a project", "Apresentar um projecto")}
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
